import { motion } from "framer-motion";
import { MapPin, Phone } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { PHONE, PHONE_LABEL, WHATSAPP_URL, COMPANY, MAPS_URL } from "@/lib/api";
import { WhatsAppIcon } from "./WhatsApp";
import { useT } from "@/lib/i18n";

const TXT = {
  no: { h2a: "Base i Stavanger.", h2b: "Hele Rogaland.", p: "Vi holder til i Stavanger og hjelper bilister i hele Rogaland med bilnøkler og chiptuning. Send oss en melding på WhatsApp for å avtale tid.", maps: "Vis i Google Maps", coord: "58.93° N · 5.73° Ø", open: `Åpent ${COMPANY.hours.toLowerCase()}`, wa: "Send melding" },
  en: { h2a: "Based in Stavanger.", h2b: "All of Rogaland.", p: "We are based in Stavanger and help drivers across Rogaland with car keys and chiptuning. Send us a message on WhatsApp to book a time.", maps: "View on Google Maps", coord: "58.93° N · 5.73° E", open: "Open mon–fri 08:00–16:00", wa: "Send message" },
};

const PLACES = ["Stavanger", "Sandnes", "Sola", "Randaberg", "Klepp", "Bryne", "Gjesdal", "Strand", "Hå", "Haugesund"];

export const Rogaland = () => {
  const t = useT(TXT);
  return (
  <section id="rogaland" data-testid="rogaland-section" className="relative overflow-hidden border-y border-white/10 bg-[var(--surface)]">
    <div className="hairline-grid pointer-events-none absolute inset-0 opacity-30" />
    <div className="pointer-events-none absolute -right-10 top-1/2 hidden -translate-y-1/2 select-none font-display text-[18vw] font-black uppercase leading-none text-white/[0.03] lg:block">
      Rogaland
    </div>
    <div className="relative mx-auto grid max-w-7xl gap-14 px-5 py-24 sm:px-8 md:py-32 lg:grid-cols-12">
      <Reveal className="lg:col-span-7">
        <Eyebrow index="05">Rogaland</Eyebrow>
        <h2 className="font-display text-5xl font-black uppercase leading-[0.9] md:text-7xl">
          {t.h2a}<br /><span className="text-[var(--red)]">{t.h2b}</span>
        </h2>
        <p className="mt-6 max-w-lg text-base leading-relaxed text-zinc-300">
          {t.p}
        </p>
        <div className="mt-10 flex flex-wrap gap-2" data-testid="rogaland-places">
          {PLACES.map((p, i) => (
            <motion.span key={p} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }} transition={{ delay: i * 0.04, duration: 0.5 }}
              className={`border px-4 py-2 font-mono text-xs uppercase tracking-wider ${p === "Stavanger" ? "border-[var(--red)] bg-[var(--red)] text-white" : "border-white/15 text-zinc-300"}`}>
              {p}
            </motion.span>
          ))}
        </div>
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-5 lg:self-center">
        <div className="clip-notch border border-white/10 bg-[#08090b]/80 p-8 backdrop-blur-xl md:p-10" data-testid="rogaland-card">
          <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-zinc-400">
            <MapPin size={16} className="text-[var(--red)]" /> {t.coord}
          </div>
          <div className="mt-6 font-display text-6xl font-black uppercase leading-none md:text-7xl">Stavanger</div>
          <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" data-testid="rogaland-maps-link" className="mt-2 block font-mono text-xs uppercase tracking-widest text-zinc-300 underline-offset-4 hover:text-white hover:underline">Jåttåveien 92 · 4020 Stavanger · {t.maps}</a>
          <div className="mt-1 font-mono text-xs uppercase tracking-widest text-zinc-400" data-testid="rogaland-hours">{t.open}</div>
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="rogaland-whatsapp-link"
            className="mt-10 flex items-center justify-center gap-3 bg-[#25D366] px-6 py-4 font-mono text-sm font-bold uppercase tracking-widest text-[#08090b] transition-transform hover:-translate-y-0.5">
            <WhatsAppIcon size={18} /> {t.wa}
          </a>
          <a href={`tel:${PHONE}`} data-testid="rogaland-phone-link"
            className="mt-3 flex items-center justify-center gap-3 border border-white/15 px-6 py-4 font-mono text-sm font-bold uppercase tracking-widest text-white transition-colors hover:border-white/50">
            <Phone size={16} /> {PHONE_LABEL}
          </a>
        </div>
      </Reveal>
    </div>
  </section>
  );
};
